import { useCallback, useEffect, useMemo } from 'react';
import { useGetUserAppTableRulesMutation } from '../services/api/utils.api';
import { TABLE_IDS, PERMISSION_TYPES, SCOPE_TYPES } from '../utils/constants';
import { useAppSelector } from '../services/hooks';
import { adminSelector } from '../services/selectors/user.selectors';
import { IUserAppTableRule } from '../services/types/utils.types';

export const useTablePermissions = () => {
  const admin = useAppSelector(adminSelector);

  const [getUserAppTableRules, { data: permissions, isLoading, isError, status }] =
    useGetUserAppTableRulesMutation();

  // Treat 'uninitialized' as loading so nothing renders before the first fetch completes.
  // This prevents downstream queries (e.g. Admin_GetUserInsertQuestionnaire) from firing
  // before we know whether the user actually has permission.
  const isReady = status === 'fulfilled' || status === 'rejected' || !admin.id;
  const effectiveIsLoading = !isReady;

  useEffect(() => {
    if (admin.id) {
      getUserAppTableRules();
    }
  }, [admin.id]);

  const resolvePermission = useCallback(
    (tableId: number): IUserAppTableRule | null => {
      if (!permissions || permissions.length === 0) return null;

      const tablePermissions = permissions.filter(
        (p: IUserAppTableRule) =>
          p.appTableId === tableId &&
          p.statusId === 1 &&
          p.scopeTypeId !== SCOPE_TYPES.ALL_THERAPISTS
      );

      if (tablePermissions.length === 0) return null;

      const userSpecificPermissions = tablePermissions.filter(
        (p: IUserAppTableRule) => p.usersId === admin.id
      );

      if (userSpecificPermissions.length > 0) {
        return userSpecificPermissions.reduce((mostPermissive, current) => {
          if (current.permissionId === PERMISSION_TYPES.EDIT) return current;
          if (mostPermissive.permissionId === PERMISSION_TYPES.EDIT) return mostPermissive;
          if (current.permissionId === PERMISSION_TYPES.READ_ONLY) return current;
          return mostPermissive;
        });
      }

      // Among global rules, prefer AllAdmins scope over AllCompany (more specific wins).
      // Among rules of the same scope level, pick the most permissive.
      const globalPermissions = tablePermissions.filter(
        (p: IUserAppTableRule) => p.usersId === null
      );
      if (globalPermissions.length === 0) return null;

      const adminScopedPermissions = globalPermissions.filter(
        (p) => p.scopeTypeId === SCOPE_TYPES.ALL_ADMINS
      );
      const candidates = adminScopedPermissions.length > 0 ? adminScopedPermissions : globalPermissions;

      return candidates.reduce((mostPermissive, current) =>
        current.permissionId > mostPermissive.permissionId ? current : mostPermissive
      );
    },
    [permissions, admin?.id]
  );

  const hasPermission = useMemo(() => {
    return (tableId: number): boolean => {
      if (effectiveIsLoading) return false;
      if (isError || !permissions || permissions.length === 0) return true;

      const permission = resolvePermission(tableId);
      if (!permission) return true;

      return permission.permissionId !== PERMISSION_TYPES.NO_PERMISSION;
    };
  }, [resolvePermission, effectiveIsLoading, isError, permissions]);

  const isReadOnly = useMemo(() => {
    return (tableId: number): boolean => {
      if (effectiveIsLoading) return true;
      if (isError || !permissions || permissions.length === 0) return false;

      const permission = resolvePermission(tableId);
      if (!permission) return false;

      return permission.permissionId === PERMISSION_TYPES.READ_ONLY;
    };
  }, [resolvePermission, effectiveIsLoading, isError, permissions]);

  const canEdit = useMemo(() => {
    return (tableId: number): boolean => {
      if (effectiveIsLoading) return false;
      if (isError || !permissions || permissions.length === 0) return true;

      const permission = resolvePermission(tableId);
      if (!permission) return true;

      return permission.permissionId === PERMISSION_TYPES.EDIT;
    };
  }, [resolvePermission, effectiveIsLoading, isError, permissions]);

  return {
    hasPermission,
    isReadOnly,
    canEdit,
    permissions,
    isLoading: effectiveIsLoading,
    isError,
    TABLE_IDS,
  };
};
